import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg, { makeWebPageSchema, makeBreadcrumbSchema } from '@/components/feature/SchemaOrg';
import { categories, featuredManhwa } from '@/mocks/home';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  const filteredManhwa = category
    ? featuredManhwa.filter((m) => m.category === slug)
    : featuredManhwa;

  const displayName = category ? category.name : '全部漫画';

  return (
    <>
      <SchemaOrg {...makeWebPageSchema(
        `${displayName} - 韩漫大全`,
        `浏览${displayName}类型的韩国漫画资源，发现优质韩漫作品。`,
        `https://www.example.com/category/${slug}`,
      )} />
      <SchemaOrg {...makeBreadcrumbSchema([
        { name: '首页', url: 'https://www.example.com/' },
        { name: displayName, url: `https://www.example.com/category/${slug}` },
      ])} />
      <Navbar />
      <main className="pt-16">
        {/* Breadcrumb */}
        <nav className="px-4 md:px-6 py-4 bg-background-100" aria-label="面包屑导航">
          <div className="max-w-7xl mx-auto flex items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-foreground-400"></i>
            <span className="text-foreground-700 font-medium">{displayName}</span>
          </div>
        </nav>

        {/* Category Tabs */}
        <section className="py-8 md:py-10 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
              {displayName}
            </h1>
            <div className="flex flex-wrap gap-2 mb-8">
              <Link
                to="/category/romance"
                className={`px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                  slug === 'romance'
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                恋爱言情
              </Link>
              <Link
                to="/category/fantasy"
                className={`px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                  slug === 'fantasy'
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                奇幻冒险
              </Link>
              <Link
                to="/category/action"
                className={`px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                  slug === 'action'
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                热血动作
              </Link>
              <Link
                to="/category/comedy"
                className={`px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                  slug === 'comedy'
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                搞笑日常
              </Link>
              <Link
                to="/category/thriller"
                className={`px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                  slug === 'thriller'
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                悬疑惊悚
              </Link>
              <Link
                to="/category/drama"
                className={`px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                  slug === 'drama'
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                剧情深度
              </Link>
              <Link
                to="/category/school"
                className={`px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                  slug === 'school'
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                校园青春
              </Link>
              <Link
                to="/category/historical"
                className={`px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                  slug === 'historical'
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                古风历史
              </Link>
            </div>

            {/* Manhwa Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="true">
              {filteredManhwa.map((item) => (
                <Link
                  key={item.id}
                  to={`/article/${item.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[2/3] rounded-xl overflow-hidden mb-3 bg-background-200">
                    <img
                      src={item.cover}
                      alt={item.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-2 right-2 px-2 py-0.5 bg-black/60 backdrop-blur-sm rounded-md flex items-center gap-1">
                      <i className="ri-star-fill text-accent-400 text-xs"></i>
                      <span className="text-white text-xs font-medium">{item.rating}</span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 to-transparent">
                      <span className="text-white/80 text-xs">
                        <i className="ri-eye-line mr-1"></i>
                        {item.views}
                      </span>
                    </div>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 text-sm md:text-base mb-1.5 group-hover:text-primary-600 transition-colors line-clamp-1">
                    {item.title}
                  </h3>
                  <p className="text-foreground-500 text-xs line-clamp-1 mb-2">{item.author}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {item.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded-md text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}